import React, { useState } from 'react';
import { 
  Calendar,
  MapPin,
  Users,
  Video,
  Mail,
  Phone,
  ArrowRight,
  CheckCircle,
  Building2,
  Mic,
  GraduationCap,
  Radio
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookSpeaking = () => {
  const [loading, setLoading] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    organization_name: '',
    contact_name: '',
    email: '',
    phone: '',
    event_type: '',
    event_date: '',
    location: '',
    virtual_or_in_person: '',
    audience_size: '',
    topic_requested: '',
    budget: '',
    additional_details: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingForm.organization_name || !bookingForm.contact_name || !bookingForm.email || !bookingForm.event_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/speaking/book`, bookingForm);
      if (response.data.success) {
        toast.success('Thank you! We\'ll be in touch within 24-48 hours to discuss your event.');
        setBookingForm({
          organization_name: '',
          contact_name: '',
          email: '',
          phone: '',
          event_type: '',
          event_date: '',
          location: '',
          virtual_or_in_person: '',
          audience_size: '',
          topic_requested: '',
          budget: '',
          additional_details: ''
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const speakingTopics = [
    {
      id: 1,
      title: "The Immigrant Journey: From $200 to Building a Movement",
      description: "A personal story of arriving in America with nothing and building a 46,000+ supporter advocacy movement"
    },
    {
      id: 2,
      title: "How Displaced Ukrainians Came to America—And What Happens Next",
      description: "Understanding the refugee journey, temporary protection status, and the need for permanent legal pathways"
    },
    {
      id: 3,
      title: "Breaking Mental Barriers: Supporting Refugees Through Integration",
      description: "The psychological challenges of displacement and practical strategies for community support"
    },
    {
      id: 4,
      title: "From Temporary Protection to Legal Uncertainty",
      description: "The policy gap leaving families in limbo and what needs to change"
    },
    {
      id: 5,
      title: "Building Community Support: One Person Can Change 46,000 Lives",
      description: "How grassroots organizing and authentic connection create systems-level change"
    },
    {
      id: 6,
      title: "Advocacy in Action: Creating Systems-Level Change",
      description: "Engaging with policymakers, mobilizing communities, and advancing legislative solutions"
    }
  ];

  const eventTypes = [
    { icon: GraduationCap, name: "Colleges & Universities", desc: "Guest lectures, panel discussions" },
    { icon: Radio, name: "TV & Media", desc: "Interviews, news segments" },
    { icon: Building2, name: "Conferences", desc: "Keynotes, workshops" },
    { icon: Users, name: "Community Events", desc: "Town halls, gatherings" }
  ];

  const scrollToBooking = () => {
    document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold">
                  Available for Speaking Engagements
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Hear Directly from
                <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"> Ukrainian Advocates</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Book Svetlana Moskvitch and Ukrainian community members to share powerful firsthand stories of displacement, resilience, and the fight for permanent legal pathways.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={scrollToBooking}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-lg px-8 py-6 shadow-xl shadow-emerald-500/30"
                >
                  Book for Your Event
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-3xl blur-3xl" />
              <img 
                src="https://customer-assets.emergentagent.com/job_advocacy-integration/artifacts/0enp7qlb_IMG_9413.jpeg"
                alt="Svetlana speaking"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Speaker */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Featured Speaker</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto rounded-full" />
          </div>

          <Card className="border-none shadow-2xl overflow-hidden">
            <div className="grid md:grid-cols-5 gap-0">
              <div className="md:col-span-2 relative h-[400px] md:h-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_advocacy-integration/artifacts/1pflywb0_IMG_9414.jpeg"
                  alt="Svetlana"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="md:col-span-3 p-8 lg:p-12">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-3xl font-bold text-slate-900 mb-2">Svetlana Moskvitch</h3>
                    <p className="text-lg text-emerald-600 font-semibold">Founder, Svetlana Foundation • Ukrainian Immigrant & Advocate</p>
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300">
                      <CheckCircle className="w-4 h-4 text-amber-700" />
                      <span className="text-sm font-bold text-amber-800">Recognized by CT Lieutenant Governor</span>
                    </div>
                  </div>
                  
                  <div className="space-y-4 text-slate-700 leading-relaxed">
                    <p>
                      Svetlana Moskvitch arrived in the United States in 1996 with just $200 in her pocket and no connections—facing the same challenges that millions of immigrants know too well. When war broke out in Ukraine four years ago, she watched as displaced families arrived with the same fears she once had: no English, no money, no one to turn to.
                    </p>
                    <p>
                      Drawing on her own journey and fluency in Ukrainian and Russian, Svetlana began helping newcomers one by one—guiding them through integration, overcoming mental barriers, finding employment, and advocating for their rights.
                    </p>
                    <p>
                      Today, as a proud U.S. citizen and founder of Svetlana Foundation, she has mobilized over <strong className="text-emerald-600">46,000 supporters</strong> to advance permanent legal pathways for displaced Ukrainians. Her transformational work has been <strong className="text-amber-700">officially recognized by Connecticut Lieutenant Governor Susan Bysiewicz</strong> for embodying compassion, courage, and a relentless drive to build a more just world.
                    </p>
                    <p className="italic text-lg font-semibold text-slate-900">
                      "Svetlana knows what it means to rebuild from nothing—and she's dedicated to ensuring others don't have to face that journey alone."
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-4 pt-4">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <span>Available for Travel</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <span>In-Person Events Nationwide</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <span>Virtual Speaking</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <span>Panel Discussions</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <span>Q&A Sessions</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Recognition & Awards */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-t-4 border-emerald-500">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 rounded-full bg-amber-100 text-amber-800 text-sm font-bold">
                🏆 OFFICIALLY RECOGNIZED
              </span>
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Recognition & Awards</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto rounded-full" />
          </div>

          <Card className="border-none shadow-2xl overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50">
            <div className="grid lg:grid-cols-2 gap-8 p-8 lg:p-12">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      Official Recognition from Connecticut Lieutenant Governor
                    </h3>
                    <p className="text-lg text-amber-700 font-semibold">
                      Susan Bysiewicz • State of Connecticut
                    </p>
                  </div>
                </div>

                <div className="space-y-4 text-slate-700 leading-relaxed bg-white p-6 rounded-xl shadow-md">
                  <p className="text-lg italic font-medium text-slate-900">
                    "I take great pleasure in offering my sincerest congratulations to <strong>Svetlana Moskvitch</strong> in recognition for transforming personal adversity into a mission to serve others since immigrating from Ukraine."
                  </p>
                  <p>
                    <strong>Recognized for:</strong>
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>Founding the Svetlana Foundation and dedicating her life to supporting vulnerable children and families</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>Providing critical resources including education, food, and mental health support</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>Embodying compassion, courage, and a relentless drive to build a more just world</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>Service to the community and the State of Connecticut</span>
                    </li>
                  </ul>
                  <p className="text-sm text-slate-500 italic pt-2">
                    — Lieutenant Governor Susan Bysiewicz, May 7, 2025
                  </p>
                </div>

                <div className="bg-emerald-50 p-6 rounded-xl border-l-4 border-emerald-600">
                  <p className="text-slate-800 font-semibold">
                    This official recognition validates Svetlana's transformational work and demonstrates her credibility as a leader in immigrant advocacy and community service.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-2xl blur-2xl" />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_advocacy-integration/artifacts/s663yuvd_image.jpg"
                    alt="Official recognition from Connecticut Lieutenant Governor"
                    className="relative rounded-2xl shadow-2xl w-full max-w-md border-4 border-white"
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Speaking Topics */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Speaking Topics</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Powerful, authentic presentations on immigration, advocacy, and community resilience
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mt-6 rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {speakingTopics.map((topic) => (
              <Card key={topic.id} className="border-none shadow-lg hover:shadow-xl transition-shadow bg-white">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 mt-1">
                      <Mic className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg leading-tight">{topic.title}</CardTitle>
                      <CardDescription className="mt-2 text-slate-600">
                        {topic.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Perfect For Your Event</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto rounded-full" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {eventTypes.map((type, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all text-center bg-gradient-to-br from-slate-50 to-emerald-50/30">
                <CardHeader>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/30">
                    <type.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">{type.name}</CardTitle>
                  <CardDescription className="text-slate-600">{type.desc}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-slate-700 mb-6">
              Svetlana Moskvitch is available for in-person and virtual events nationwide
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-emerald-600" />
                <span className="text-slate-700">Will Travel Nationwide</span>
              </div>
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-emerald-600" />
                <span className="text-slate-700">Virtual Presentations</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" />
                <span className="text-slate-700">Panel Discussions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Book Svetlana Moskvitch for Your Event</h2>
            <p className="text-xl text-slate-600">
              Fill out the form below and we'll be in touch within 24-48 hours
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mt-6 rounded-full" />
          </div>

          <Card className="border-none shadow-2xl bg-white/95 backdrop-blur-md">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Organization Name *
                    </label>
                    <Input
                      required
                      value={bookingForm.organization_name}
                      onChange={(e) => setBookingForm({...bookingForm, organization_name: e.target.value})}
                      placeholder="University, Company, etc."
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Contact Name *
                    </label>
                    <Input
                      required
                      value={bookingForm.contact_name}
                      onChange={(e) => setBookingForm({...bookingForm, contact_name: e.target.value})}
                      placeholder="Your name"
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email *
                    </label>
                    <Input
                      required
                      type="email"
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                      placeholder="your@email.com"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Phone
                    </label>
                    <Input
                      type="tel"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Event Type *
                    </label>
                    <select
                      required
                      value={bookingForm.event_type}
                      onChange={(e) => setBookingForm({...bookingForm, event_type: e.target.value})}
                      className="w-full h-12 px-3 rounded-md border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Select event type</option>
                      <option value="college">College/University</option>
                      <option value="tv_media">TV/Media Interview</option>
                      <option value="conference">Conference/Summit</option>
                      <option value="community">Community Event</option>
                      <option value="nonprofit">Nonprofit Gathering</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Preferred Date
                    </label>
                    <Input
                      type="date"
                      value={bookingForm.event_date}
                      onChange={(e) => setBookingForm({...bookingForm, event_date: e.target.value})}
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Location/City
                    </label>
                    <Input
                      value={bookingForm.location}
                      onChange={(e) => setBookingForm({...bookingForm, location: e.target.value})}
                      placeholder="City, State"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Format
                    </label>
                    <select
                      value={bookingForm.virtual_or_in_person}
                      onChange={(e) => setBookingForm({...bookingForm, virtual_or_in_person: e.target.value})}
                      className="w-full h-12 px-3 rounded-md border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Select format</option>
                      <option value="in_person">In-Person</option>
                      <option value="virtual">Virtual (Zoom/Online)</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Expected Audience Size
                    </label>
                    <Input
                      value={bookingForm.audience_size}
                      onChange={(e) => setBookingForm({...bookingForm, audience_size: e.target.value})}
                      placeholder="e.g., 50-100 people"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Budget/Speaking Fee
                    </label>
                    <Input
                      value={bookingForm.budget}
                      onChange={(e) => setBookingForm({...bookingForm, budget: e.target.value})}
                      placeholder="Your budget range"
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Topic(s) Requested
                  </label>
                  <Input
                    value={bookingForm.topic_requested}
                    onChange={(e) => setBookingForm({...bookingForm, topic_requested: e.target.value})}
                    placeholder="Which speaking topic interests you?"
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Additional Details
                  </label>
                  <Textarea
                    value={bookingForm.additional_details}
                    onChange={(e) => setBookingForm({...bookingForm, additional_details: e.target.value})}
                    placeholder="Tell us more about your event, audience, goals, etc."
                    className="min-h-[120px]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 text-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30"
                >
                  {loading ? 'Submitting...' : 'Submit Booking Request'}
                  {!loading && <Calendar className="ml-2 w-5 h-5" />}
                </Button>

                <p className="text-sm text-center text-slate-500">
                  We'll respond within 24-48 hours to confirm availability and discuss details
                </p>
              </form>
            </CardContent>
          </Card>

          <div className="mt-12 text-center space-y-4">
            <p className="text-lg font-semibold text-slate-900">Or contact us directly:</p>
            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <div className="flex items-center justify-center gap-2 text-slate-700">
                <Mail className="w-5 h-5 text-emerald-600" />
                <a href="mailto:moskvitchsvetlana@gmail.com" className="hover:text-emerald-600">moskvitchsvetlana@gmail.com</a>
              </div>
              <div className="flex items-center justify-center gap-2 text-slate-700">
                <Phone className="w-5 h-5 text-emerald-600" />
                <a href="tel:+14137773136" className="hover:text-emerald-600">(413) 777-3136</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookSpeaking;